import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Users, CheckCircle, Loader2, CreditCard, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AffiliateSignupPage = () => {
  const [loading, setLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    business_type: "hotel",
    website: "",
    how_promote: ""
  });

  const handleStripeConnect = async () => {
    setStripeLoading(true);
    try {
      const response = await axios.get(`${API}/affiliate/stripe-onboard/${affiliateCode}`);
      if (response.data.already_onboarded) {
        toast.success("Your Stripe account is already connected!");
      } else if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      toast.error("Failed to start Stripe onboarding. Please try again later.");
    } finally {
      setStripeLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/affiliate-applications`, formData);
      setAffiliateCode(response.data.affiliate_code);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-[#047857]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#047857]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl text-white mb-4">
            Welcome to Advanta Plus!
          </h1>
          <p className="text-white/50 mb-6">
            Your affiliate account has been created. You can start promoting right away!
          </p>
          
          <div className="bg-[#0B0F19] rounded-xl p-6 border border-[#D4AF37]/20 mb-6">
            <p className="text-white/50 text-sm mb-2">Your Affiliate Code</p>
            <p className="font-mono text-[#D4AF37] text-2xl">{affiliateCode}</p>
          </div>

          {/* Stripe Connect Section */}
          <div className="bg-gradient-to-r from-[#635BFF]/10 to-[#635BFF]/5 rounded-xl p-6 border border-[#635BFF]/30 mb-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <CreditCard className="w-5 h-5 text-[#635BFF]" />
              <h3 className="text-white font-medium">Connect Your Bank Account</h3>
            </div>
            <p className="text-white/50 text-sm mb-4">
              Connect with Stripe to receive your 20% commission payouts directly to your bank account.
            </p>
            <Button 
              onClick={handleStripeConnect}
              disabled={stripeLoading}
              className="w-full bg-[#635BFF] hover:bg-[#635BFF]/90 text-white py-4"
            >
              {stripeLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                  </svg>
                  Connect with Stripe
                  <ExternalLink className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            <p className="text-white/30 text-xs mt-3">
              You can also do this later from your dashboard
            </p>
          </div>
          
          <Link to={`/affiliate/${affiliateCode}`}>
            <Button className="btn-gold px-8 py-4 rounded w-full mb-4">
              Go to Your Dashboard
            </Button>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-white/50 hover:text-white">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] py-12 px-6">
      {/* Back Button */}
      <Link 
        to="/"
        className="fixed top-6 left-6 flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors z-10"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Back</span>
      </Link>

      <div className="max-w-2xl mx-auto pt-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl text-white mb-2">
            Become an Affiliate
          </h1>
          <p className="text-white/50">
            Earn 20% commission on every membership sold through your referral link
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-3 gap-4 mb-10">
          {[
            { title: "20%", desc: "Commission per sale" },
            { title: "Instant", desc: "Tracking & attribution" },
            { title: "Automatic", desc: "Stripe payouts" },
          ].map((benefit, i) => (
            <div key={i} className="bg-[#0B0F19] rounded-xl p-4 text-center border border-[#D4AF37]/10">
              <p className="text-[#D4AF37] font-bold text-xl">{benefit.title}</p>
              <p className="text-white/40 text-xs mt-1">{benefit.desc}</p>
            </div>
          ))}
        </div>

        {/* Earnings Example */}
        <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#047857]/10 rounded-xl p-6 border border-[#D4AF37]/20 mb-10">
          <h3 className="text-white font-medium mb-3">Potential Earnings Example</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-white/40 text-xs">10 sales/month</p>
              <p className="text-white font-bold">€90/month</p>
            </div>
            <div>
              <p className="text-white/40 text-xs">50 sales/month</p>
              <p className="text-[#D4AF37] font-bold">€450/month</p>
            </div>
            <div>
              <p className="text-white/40 text-xs">100 sales/month</p>
              <p className="text-[#047857] font-bold">€900/month</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-[#0B0F19] rounded-2xl p-8 border border-[#D4AF37]/10">
          <h2 className="font-['Playfair_Display'] text-xl text-white mb-6">Your Information</h2>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <Label className="text-white/70">Your Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="John Smith"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                placeholder="john@email.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
          </div>

          <div className="space-y-2 mb-6">
            <Label className="text-white/70">Phone Number</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="+34 612 345 678"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>

          <h2 className="font-['Playfair_Display'] text-xl text-white mb-6">Business Details (Optional)</h2>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <Label className="text-white/70">Business/Organization Name</Label>
              <Input
                value={formData.business_name}
                onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                placeholder="Hotel Marbella"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Business Type</Label>
              <Select value={formData.business_type} onValueChange={(v) => setFormData({...formData, business_type: v})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hotel">Hotel / Accommodation</SelectItem>
                  <SelectItem value="tour_operator">Tour Operator</SelectItem>
                  <SelectItem value="travel_agent">Travel Agent</SelectItem>
                  <SelectItem value="influencer">Influencer / Blogger</SelectItem>
                  <SelectItem value="concierge">Concierge Service</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <Label className="text-white/70">Website / Social Media</Label>
            <Input
              value={formData.website}
              onChange={(e) => setFormData({...formData, website: e.target.value})}
              placeholder="https://www.yourwebsite.com or @instagram"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>

          <div className="space-y-2 mb-8">
            <Label className="text-white/70">How will you promote Advanta Plus?</Label>
            <textarea
              value={formData.how_promote}
              onChange={(e) => setFormData({...formData, how_promote: e.target.value})}
              placeholder="E.g., QR codes at reception, email to guests, social media posts..."
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <Button 
            type="submit" 
            className="btn-gold w-full py-6 rounded text-base"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Creating Account...
              </>
            ) : (
              "Create Affiliate Account"
            )}
          </Button>

          <p className="text-white/30 text-xs text-center mt-4">
            By signing up, you agree to our affiliate terms. Commission payouts require connecting a Stripe account.
          </p>
        </form>
      </div>
    </div>
  );
};

export default AffiliateSignupPage;
